import React, { useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import styles from "./index.module.scss";
import { useServices } from "@/services-context";
import type { B2cProductPayload, B2cProductPrice } from "@/generated/graphql/b2c";
import { Icon } from "@/elements-react/fix-icon";
import FixOptionsLink from "@/elements-react/fix-options-link";
import { createProductUrl } from "@/util/category";
import { Url } from "@/helpers/url";
import { Gtag } from "@/services/gtag.ts";
import { getCategoriesEcommerceItems, getItemName } from "@/helpers/google-events.ts";

type OptionItem = {
  id: string;
  name: string;
  slug: string;
  mpn: string;
  size: string;
  price: number;
  time: string;
  variantsCount: number;
  collapsed: boolean;
  rawPrices: B2cProductPrice[];
};

type Dataset = {
  key: "original" | "premium" | "budget";
  title: string;
  prices: string;
  options: OptionItem[];
  empty: boolean;
}[];

interface ProductCanBeProps {
  type?: "lg" | "sm";
  data: Array<B2cProductPayload>;
  active?: string | null;
}

const groups = ["original", "premium", "budget"] as const;

const ProductCanBe: React.FC<ProductCanBeProps> = ({
  type = "lg",
  data,
  active: activeProp = null,
}) => {
  const { t } = useTranslation();
  const { formatter, categories } = useServices();
  const [active, setActive] = useState<string | null>(activeProp);
  const [expandedItem, setExpandedItem] = useState<string[]>([]);
  const services = useServices();

  // --- helpers ---
  const getGroupValues = useCallback(
    (key: "original" | "premium" | "budget") => {
      switch (key) {
        case "original":
          return { title: t("yh809i5831915t0u.original", "Original"), type: "oe" };
        case "premium":
          return { title: t("88y21tbl8qz11dcg.premium-analogue", "Premium analogue"), type: "oem" };
        case "budget":
          return { title: t("oz9f39a92yrf0j8t.budget-analogue", "Budget analogue"), type: "aftermarket" };
      }
    },
    [t]
  );

  const getGroupItems = useCallback(
    (type: string) =>
      data.filter((item) => {
        const productType = item.productType || item.brand.defaultProductType;
        switch (type) {
          case "original":
            return productType === "oe";
          case "premium":
            return productType === "oem";
          case "budget":
            return productType === "aftermarket";
        }
        return productType === null;
      }),
    [data]
  );

  const getPricesText = useCallback(
    (items: B2cProductPayload[]) => {
      const amounts: number[] = [];
      for (const item of items) {
        for (const price of item.prices) {
          amounts.push(parseFloat(price.price.amount));
        }
      }
      const max = Math.max(...amounts, 0);
      const min = Math.min(...amounts, max);
      return formatter.priceRange([min, max]);
    },
    [formatter]
  );

  const getMinPrice = useCallback(
    (prices: B2cProductPayload["prices"]) =>
      prices.slice().sort((a, b) => a.price.amount - b.price.amount)[0],
    []
  );

  const deliveryTermsRange = useCallback(
    (prices: B2cProductPrice[]) => {
      let deliveryMin = 0,
        deliveryMax = 0;
      for (const price of prices) {
        if (price.deliveryTermStandardMin < deliveryMin || deliveryMin === 0) {
          deliveryMin = price.deliveryTermStandardMin;
        }
        if (price.deliveryTermStandardMax > deliveryMax) {
          deliveryMax = price.deliveryTermStandardMax;
        }
      }
      if (deliveryMin === 0 && deliveryMax === 0) return "-";
      if (deliveryMin === deliveryMax)
        return t("rfje423vfw3fj4hv.delivery-term", "{{deliveryTerm}} days", {
          deliveryTerm: deliveryMin,
        });
      return t(
        "rfje21tj4m4fj4hv.delivery-terms-range",
        "{{minTerm}}-{{maxTerm}} days",
        { minTerm: deliveryMin, maxTerm: deliveryMax }
      );
    },
    [t]
  );

  const createOptions = useCallback(
    (crosses: B2cProductPayload[], crossesType: string) => {
      const options: Record<string, OptionItem[]> = {};
      crosses.forEach((cross) => {
        options[cross.brand.slug] ??= [];
        options[cross.brand.slug].push({
          id: cross.id,
          mpn: cross.mpn ?? cross.mpnFormatted,
          size: "",
          variantsCount: 1,
          time: deliveryTermsRange(cross.prices),
          name: cross.brand.name,
          slug: cross.brand.slug,
          price: getMinPrice(cross.prices)?.price.amount ?? 0,
          collapsed: false,
          rawPrices: cross.prices,
        });
      });

      const groupedOptions: OptionItem[] = [];
      for (const key in options) {
        if (options[key].length > 1) {
          const groupId = crossesType + "_" + options[key][0].slug;
          groupedOptions.push({
            id: groupId,
            mpn: "",
            size: "",
            variantsCount: options[key].length,
            time: deliveryTermsRange(
              options[key].reduce(
                (acc: B2cProductPrice[], item: OptionItem) =>
                  acc.concat(item.rawPrices),
                []
              )
            ),
            name: options[key][0].name,
            slug: options[key][0].slug,
            price: Math.min(...options[key].map((item) => item.price)),
            collapsed: !expandedItem.includes(groupId),
            rawPrices: [],
          });

          if (expandedItem.includes(groupId)) {
            const subItems = options[key]
              .sort((a, b) => a.price - b.price)
              .map((item) => ({ ...item, variantsCount: 0 }));
            groupedOptions.push(...subItems);
          }
        } else {
          groupedOptions.push(options[key][0]);
        }
      }
      return groupedOptions;
    },
    [deliveryTermsRange, getMinPrice, expandedItem]
  );

  // --- dataset ---
  const calculatedDataset: Dataset = useMemo(() => {
    const result: Dataset = [];
    for (const key of groups) {
      const { title, type: groupType } = getGroupValues(key);
      const items = getGroupItems(key);
      const options = createOptions(items, key);
      const prices = getPricesText(items);
      result.push({
        key,
        title,
        prices,
        options,
        empty: options.length === 0,
      });
    }
    return result;
  }, [getGroupValues, getGroupItems, createOptions, getPricesText]);

  const onOptionClick = useCallback((id: string) => {
    const [type, brand] = id.split("_");
    if (["original", "premium", "budget"].includes(type) && brand) {
      setExpandedItem((prev) =>
        prev.includes(id)
          ? prev.filter((collapsedId) => id !== collapsedId)
          : [...prev, id]
      );
    } else {
      setActive(id);
      const product = data.find((item) => item.id === id)!;
      const url = product.url ? product.url : createProductUrl(product, categories);

      Gtag.pushEventEcommerce("select_item", {
        items: [{
          item_name: getItemName(product),
          item_id: product.id,
          price: product.prices[0].price.amount,
          item_brand: product.brand.name,
          item_list_name: "Analogues",
          index: 0,
          quantity: "1",
          ...getCategoriesEcommerceItems(product.categories, services),
        }],
      });

      Url.route(url);
    }
  }, [data, categories]);

  // --- renderOption ---
  const renderOption = useCallback(
    (item: OptionItem) => (
      <>
        {item.variantsCount === 1 && (
          <div className={classNames(styles.name, styles.leftAlign)}>
            {item.name}
            <div className={styles.secondarySm}>{item.mpn}</div>
          </div>
        )}
        {item.variantsCount > 1 && (
          <div className={classNames(styles.name, styles.leftAlign)}>
            {item.name}
            <div className={classNames(styles.secondary, styles.linkColor)}>
              {item.collapsed
                ? t("dsnm9sd7fs6cjksd7.variants", "{{count}} variants", {
                  count: item.variantsCount,
                })
                : t("ppfsd9865sdf4567d.hide", "hide")}
              <Icon name={item.collapsed ? "chevron-down" : "chevron-up"} />
            </div>
          </div>
        )}
        {item.variantsCount === 0 && (
          <div className={styles.secondary}>{item.mpn}</div>
        )}
        <div className={styles.time}>{item.time}</div>
        <div className={styles.price}>
          {t("flw8dfds23slsiyy2.from", "from")}
          <span className={styles.bold}>
            {formatter.price(item.price, "full", true)}
          </span>
        </div>
      </>
    ),
    [t, formatter]
  );

  // --- renderProductOptions ---
  const renderProductOptions = (type: string) => {
    const item = calculatedDataset.find((item) => item.key === type);
    if (!item || item.options.length === 0) return null;
    return (
      <div>
        <div className={styles.title}>{item.title}</div>
        <FixOptionsLink
          options={item.options}
          active={active}
          renderer={renderOption}
          direction="vertical"
          onUpdateActive={onOptionClick}
        />
      </div>
    );
  };

  return (
    <div className={classNames(styles.root, styles[type])}>
      {renderProductOptions("original")}
      {renderProductOptions("premium")}
      {renderProductOptions("budget")}
    </div>
  );
};

export default ProductCanBe;

